// Enhanced Checkpoint Card component with dynamic lane wait times and permission-based UI control
import React from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { getRelativeTime, formatHawaiiDateTime } from '../utils/helpers';
import { canView, canUpdate, getAccessLevel, permissionChecks } from '../lib/auth/helpers';
import { PermissionGuard } from '../lib/auth/helpers';

export interface QueueTime {
  id: string;
  name: string;
  type: 'standard' | 'clear' | 'tsa-precheck' | 'priority' | 'premium' | 'express' | 'other';
  waitTime: number | null; // in minutes, null means closed
  isActive: boolean;
}

export interface CheckpointCardProps {
  id: number;
  name: string;
  description: string;
  terminalName: string;
  isOpen: boolean;
  isException: boolean;
  sectionsCount: number;
  assetsCount: number;
  queueCount: number;
  lastUpdated: string;
  updatedBy: string;
  queueTimes: QueueTime[];
  isToggling?: boolean;
  isDisplayShutdown?: boolean;
  onToggleStatus: (e: React.MouseEvent, checkpointId: number) => void;
  onClick: (checkpointId: number) => void;
}

// Basic queue type styles
const queueTypeStyles = {
  'standard': 'bg-yellow-500 text-gray-900',
  'clear': 'bg-white text-gray-900',
  'tsa-precheck': 'bg-blue-500 text-white',
  'priority': 'bg-cyan-500 text-white',
  'premium': 'bg-purple-500 text-white',
  'express': 'bg-green-500 text-white',
  'other': 'bg-gray-500 text-white'
};

export const CheckpointCard: React.FC<CheckpointCardProps> = ({
  id,
  name,
  description,
  terminalName,
  isOpen,
  isException,
  sectionsCount,
  assetsCount,
  queueCount,
  lastUpdated,
  updatedBy,
  queueTimes,
  isToggling = false,
  isDisplayShutdown = false,
  onToggleStatus,
  onClick
}) => {
  // Use new feature-based permission system
  const canViewCheckpoint = canView('checkpoint');
  const canUpdateCheckpoint = canUpdate('checkpoint');
  const canToggleCheckpointStatus = permissionChecks.canToggleCheckpointStatus();
  const canManageCheckpointExceptions = permissionChecks.canManageCheckpointExceptions();
  
  // Check for new dashboard checkpoint status update permission
  const canUpdateCheckpointStatus = permissionChecks.canUpdateCheckpointStatus();
  
  // Check for new queue permissions
  const canViewQueuePage = permissionChecks.canViewQueuePage();
  
  // NOTE: Checkpoint card should always be visible - only the status toggle is controlled by permissions
  // Removed the early return for canViewCheckpoint - cards will always render

  const renderQueueItem = (queue: QueueTime, index: number) => {
    // Get styles based on queue type
    const styles = queueTypeStyles[queue.type] || 'bg-gray-500 text-white';
    
    // Display logic: show wait time or "CLOSED"
    const waitTimeDisplay = queue.waitTime !== null ? `${queue.waitTime} min` : 'CLOSED';
    
    // Check if user can interact with queue items
    const canInteractWithQueue = canView('queue');
    
    return (
      <div
        key={queue.id}
        className={`${styles} rounded px-3 py-2 text-center text-sm font-medium transition-colors ${
          canInteractWithQueue 
            ? 'hover:opacity-80 cursor-pointer' 
            : 'cursor-default'
        }`}
      >
        <div className="text-xs font-semibold uppercase tracking-wide mb-1">
          {queue.name}
        </div>
        <div className="text-sm font-bold">
          {waitTimeDisplay}
        </div>
      </div>
    );
  };

  return (
    <div 
      onClick={canViewQueuePage ? () => onClick(id) : undefined}
      className={`bg-gray-800 rounded-lg p-6 relative transition-all duration-200 ${
        canViewQueuePage 
          ? 'cursor-pointer hover:bg-gray-750 hover:shadow-lg transform hover:-translate-y-1' 
          : 'cursor-default opacity-75'
      }`}
    >
      {/* Status Indicator */}
      <div 
        className={`absolute top-4 right-4 w-3 h-3 rounded-full ${
          isOpen ? 'bg-green-500' : 'bg-red-500'
        }`}
      />

      {/* Header Section */}
      <div className="mb-4">
        <h2 className="text-xl font-bold text-white mb-1">
          {name}
        </h2>
        <p className="text-sm text-gray-400 mb-1">{terminalName}</p>
        <p className="text-sm text-gray-400">{description}</p>
      </div>

      {/* Status Toggle - Only show if user has dashboard_checkpoint_status_update permission */}
      {canUpdateCheckpointStatus && (
        <div className="flex items-center justify-between mb-4 py-2 border-b border-gray-700">
          <span className="text-sm font-medium text-gray-300">Status</span>
          <button
            onClick={(e) => onToggleStatus(e, id)}
            disabled={isToggling}
            className={`
              relative inline-flex h-6 w-11 items-center rounded-full 
              transition-colors duration-200 focus:outline-none focus:ring-2 
              focus:ring-offset-2 focus:ring-offset-gray-800
              ${isToggling 
                ? 'bg-gray-500 cursor-not-allowed' 
                : isOpen 
                ? 'bg-green-600 focus:ring-green-500' 
                : 'bg-gray-600 focus:ring-gray-500'
              }
            `}
          >
            <span
              className={`
                inline-block h-4 w-4 transform rounded-full bg-white 
                transition-transform duration-200
                ${isOpen ? 'translate-x-6' : 'translate-x-1'} 
                ${isToggling ? 'animate-pulse' : ''}
              `}
            />
          </button>
        </div>
      )}
      
      {/* Status Display - Show when dashboard_checkpoint_status_update is false (read-only) */}
      {!canUpdateCheckpointStatus && (
        <div className="flex items-center justify-between mb-4 py-2 border-b border-gray-700">
          <span className="text-sm font-medium text-gray-300">Status</span>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
            <span className="text-sm text-gray-400">
              {isOpen ? 'Open' : 'Closed'}
            </span>
          </div>
        </div>
      )}

      {/* Basic Queue Times Section */}
      <div className="mb-4">
        {queueTimes.length === 0 ? (
          <div className="bg-gray-700 rounded px-3 py-3 text-center text-gray-300">
            <div className="text-xs font-semibold uppercase tracking-wide mb-1">
              No Queue Data
            </div>
            <div className="text-sm font-bold">
              - -
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {queueTimes.map((queue, index) => renderQueueItem(queue, index))}
          </div>
        )}
      </div>

      {/* Stats Section */}
      <div className="grid grid-cols-3 gap-4 mb-4 pt-4 border-t border-gray-700">
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Sections</div>
          <div className="text-lg font-bold text-white">{sectionsCount}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Assets</div>
          <div className="text-lg font-bold text-white">{assetsCount}</div>
        </div>
        <div className="text-center">
          <div className="text-xs text-gray-400 uppercase tracking-wide mb-1">Queues</div>
          <div className="text-lg font-bold text-white">{queueTimes.length}</div>
        </div>
      </div>

      {/* Updated Info */}
      <div className="text-xs text-gray-500 text-center">
        Updated {formatHawaiiDateTime(lastUpdated)} by {updatedBy}
      </div>

      {/* Queue Access Indicator - Show when queue_view is false */}
      {!canViewQueuePage && (
        <div className="text-xs text-gray-500 text-center mt-2">
          Queue access restricted
        </div>
      )}

      {/* Exception Indicator - Only show if user has exception management permission */}
      {isException && canManageCheckpointExceptions && (
        <div className="absolute top-4 left-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
        </div>
      )}
    </div>
  );
};